import { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Stack,
    CircularProgress,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { Paper } from '@shared/ui/common/paper';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { useConnections } from '@shared/hooks';
import { useGenericDrawer } from '@shared/shell/drawer/useGenericDrawer';
import { useShopifySettings, useShopifySync } from '../hooks/useShopifyConnector';
import ConnectionActiveImports from '../../generic/components/ConnectionActiveImports';
import { ACTIVE_IMPORT_JOBS_QUERY_KEY } from '../../hooks/useActiveImportJobs';
import { APP_CONFIG } from '../../../../config/app.config';
import {
    DEFAULT_SHOPIFY_PRODUCT_MATCH_MAPPING,
    resolveShopifyProductMatchMapping,
    ShopifyProductMatchMapping,
} from '@domain/connection/models/ConnectionSettings';
import { useToastStore } from '../../../../application/store/toastStore';

import { useCustomFields } from '@features/products/hooks/useCustomFields';
import {
    SHOPIFY_PRODUCT_SOURCE_FIELDS,
    JUSTSTOCK_PRODUCT_TARGET_FIELDS,
    customFieldTargetPath,
    customFieldTargetLabel,
} from '@shared/connection/shopify/constants/shopifyProductFields';

interface ShopifyConnectionSettingsProps {
    connectionId: string;
}

const ShopifyConnectionSettings = ({ connectionId }: ShopifyConnectionSettingsProps) => {
    const { onClose } = useGenericDrawer();
    const queryClient = useQueryClient();
    const { connections, removeConnection } = useConnections();
    const connection = connections?.find(c => c.id === connectionId);

    const { settingsResponse, isLoading, updateSettings, isUpdating } = useShopifySettings(connectionId);
    const { syncConnection, syncProducts, syncOrders, syncInventory, isSyncing } = useShopifySync();
    const { customFields: customFieldsResponse } = useCustomFields();

    const [mapping, setMapping] = useState<ShopifyProductMatchMapping>(
        DEFAULT_SHOPIFY_PRODUCT_MATCH_MAPPING,
    );
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [activeSync, setActiveSync] = useState<string | null>(null);
    const addToast = useToastStore(state => state.addToast);

    const companyCustomFields = customFieldsResponse?.status === 'success'
        ? customFieldsResponse.data
        : [];

    const targetFieldOptions = [
        ...JUSTSTOCK_PRODUCT_TARGET_FIELDS,
        ...companyCustomFields.map((field) => ({
            path: customFieldTargetPath(field.key),
            label: customFieldTargetLabel(field.label),
        })),
    ];

    if (
        mapping.targetField &&
        !targetFieldOptions.some((field) => field.path === mapping.targetField)
    ) {
        targetFieldOptions.push({
            path: mapping.targetField,
            label: mapping.targetField.startsWith('customFields.')
                ? mapping.targetField.slice('customFields.'.length)
                : mapping.targetField,
        });
    }

    useEffect(() => {
        if (settingsResponse) {
            setMapping(resolveShopifyProductMatchMapping(settingsResponse.import));
        }
    }, [settingsResponse]);

    const savedMapping = resolveShopifyProductMatchMapping(settingsResponse?.import);
    const isDirty = !!settingsResponse && (
        mapping.sourceField !== savedMapping.sourceField ||
        mapping.targetField !== savedMapping.targetField
    );

    const handleSave = async () => {
        try {
            await updateSettings({
                import: {
                    productMatchMapping: mapping,
                },
            });
            addToast('Settings saved', 'success');
        } catch {
            addToast('Failed to save settings', 'error');
        }
    };

    const syncActions: { key: string; label: string; run: () => Promise<unknown> }[] = [
        { key: 'products', label: 'Sync Products', run: () => syncProducts(connectionId) },
        { key: 'inventory', label: 'Sync Inventory', run: () => syncInventory(connectionId) },
        { key: 'orders', label: 'Sync Orders', run: () => syncOrders(connectionId) },
        { key: 'full', label: 'Full Sync', run: () => connection ? syncConnection(connection) : Promise.resolve() },
    ];

    const handleSync = async (action: typeof syncActions[number]) => {
        setActiveSync(action.key);
        try {
            await action.run();
            await queryClient.invalidateQueries({ queryKey: ACTIVE_IMPORT_JOBS_QUERY_KEY });
            addToast(`${action.label} started`, 'success');
        } catch {
            addToast(`${action.label} failed`, 'error');
        } finally {
            setActiveSync(null);
        }
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await removeConnection(connectionId);
            addToast('Connection deleted', 'success');
            onClose();
        } catch {
            addToast('Failed to delete connection', 'error');
            setIsDeleting(false);
        }
    };

    return (
        <>
            <Stack
                direction="row"
                position="sticky"
                top={0}
                p={4}
                zIndex={1}
                bgcolor="background.paper"
                justifyContent="space-between"
                alignItems="center"
            >
                <Stack direction="row" alignItems="center" spacing={2}>
                    {connection && (
                        <Avatar
                            variant="rounded"
                            src={connection.connector.image}
                            alt={connection.connector.type}
                            sx={{ width: 36, height: 36, border: 1, borderColor: 'divider' }}
                            slotProps={{ img: { style: { objectFit: 'contain' } } }}
                        />
                    )}
                    <Typography variant="h6">{connection?.name ?? 'Connection Settings'}</Typography>
                </Stack>
                {onClose && (
                    <IconButton onClick={onClose}>
                        <Close />
                    </IconButton>
                )}
            </Stack>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, px: 4, pb: 4 }}>

                {/* Import Settings */}
                <Paper>
                    <DialogHeader>
                        <Typography variant="h6">Import Settings</Typography>
                    </DialogHeader>
                    <Box sx={{ p: 3 }}>
                        {isLoading ? (
                            <Box display="flex" justifyContent="center" py={2}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <Stack spacing={3}>
                                <Box>
                                    <Typography variant="subtitle2">Product field mapping</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Choose which Shopify product field is matched to which Juststock product field when importing products, inventory, and orders.
                                    </Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel id="shopify-source-field-label">Shopify field</InputLabel>
                                            <Select
                                                labelId="shopify-source-field-label"
                                                label="Shopify field"
                                                value={mapping.sourceField}
                                                onChange={(event) => setMapping((current) => ({
                                                    ...current,
                                                    sourceField: event.target.value,
                                                }))}
                                                disabled={isUpdating}
                                            >
                                                {SHOPIFY_PRODUCT_SOURCE_FIELDS.map((field) => (
                                                    <MenuItem key={field.path} value={field.path}>
                                                        {field.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl fullWidth size="small">
                                            <InputLabel id="juststock-target-field-label">Juststock field</InputLabel>
                                            <Select
                                                labelId="juststock-target-field-label"
                                                label="Juststock field"
                                                value={mapping.targetField}
                                                onChange={(event) => setMapping((current) => ({
                                                    ...current,
                                                    targetField: event.target.value,
                                                }))}
                                                disabled={isUpdating}
                                            >
                                                {targetFieldOptions.map((field) => (
                                                    <MenuItem key={field.path} value={field.path}>
                                                        {field.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Stack>
                                </Box>

                                <Box>
                                    <Button
                                        variant="contained"
                                        onClick={handleSave}
                                        disabled={isLoading || isUpdating || !isDirty}
                                        startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : undefined}
                                    >
                                        {isUpdating ? 'Saving…' : 'Save changes'}
                                    </Button>
                                    {!isDirty && !isUpdating && (
                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                            All changes saved
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>
                        )}
                    </Box>
                </Paper>

                {/* Sync Actions */}
                <Paper>
                    <DialogHeader>
                        <Typography variant="h6">Sync</Typography>
                    </DialogHeader>
                    <Box sx={{ p: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Syncs run in the background and can take a few minutes for large catalogs.
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                            {syncActions.map(action => (
                                <Button
                                    key={action.key}
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleSync(action)}
                                    disabled={activeSync !== null || isSyncing || connection?.authorized === false}
                                    startIcon={activeSync === action.key
                                        ? <CircularProgress size={14} color="inherit" />
                                        : undefined}
                                >
                                    {activeSync === action.key ? 'Starting…' : action.label}
                                </Button>
                            ))}
                        </Stack>
                    </Box>
                </Paper>

                <Paper>
                    <DialogHeader>
                        <Typography variant="h6">Active Imports</Typography>
                    </DialogHeader>
                    <Box sx={{ p: 0 }}>
                        <ConnectionActiveImports connectionId={connectionId} />
                    </Box>
                </Paper>

                {/* Danger Zone */}
                <Paper>
                    <DialogHeader>
                        <Typography variant="h6" color="error.main">Danger Zone</Typography>
                    </DialogHeader>
                    <Box sx={{ p: 3 }}>
                        <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                                Deleting this connection will remove all associated listings and data. This action cannot be undone.
                            </Typography>
                            <Box>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={() => setDeleteDialogOpen(true)}
                                >
                                    Delete Connection
                                </Button>
                            </Box>
                        </Stack>
                    </Box>
                </Paper>

            </Box>

            {/* Delete Confirmation */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => !isDeleting && setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        borderBottom: `1px solid ${APP_CONFIG.ui.theme.colors.border}`,
                        p: 3,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h6">Confirm Delete</Typography>
                    <IconButton onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 3, mt: 3 }}>
                    <Typography>
                        Are you sure you want to delete <strong>{connection?.name}</strong>? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `1px solid ${APP_CONFIG.ui.theme.colors.border}` }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined" disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        variant="contained"
                        color="error"
                        disabled={isDeleting}
                        startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        {isDeleting ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ShopifyConnectionSettings;
